import React, { useRef, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { Dog, Plus, Search, X, ImagePlus, ArrowLeft, CalendarDays } from "lucide-react";
import { createFeedComment, getFeedComments } from "../../services/feedCommentService";
import { normalizeFeedComment, mergeFetchedComments } from "../../models/feedCommentModel";
import { normalizeFeed } from "../../models/feedModel";
import { createFeed, getAllFeeds, getFeedById } from "../../services/feedService";
import TopNavBar from "../../components/TopNavBar";
import appIcon from "../../assets/appIcon.svg";
import composeAvatar from "../../assets/feed/compose-avatar.svg";
import cardAvatar from "../../assets/feed/card-avatar.svg";
import userPlaceholder from "../../assets/userPlaceholder.png";
import "./FeedPage.css";
import { getAllEvents, bookEvent, unjoinEvent } from "../../services/eventService";
import sendIcon from "../../assets/feed/send.svg";
import closeComposerIcon from "../../assets/feed/close-composer.svg";
import removePhotoIcon from "../../assets/feed/remove-photo.svg";
import boneIcon from "../../assets/feed/bone.svg";
import commentIcon from "../../assets/feed/comment.svg";
import shareIcon from "../../assets/feed/share.svg";
import dogSelectedIcon from "../../assets/feed/dog-selected.svg";
import dogUnselectedIcon from "../../assets/feed/dog-unselected.svg";
import eventsIcon from "../../assets/feed/events.svg";
import filterToggleIcon from "../../assets/feed/filter-toggle.svg";

const categories = ["All", "Announcements", "Introductions", "Questions", "Requests", "Events"];


const categoryHeadings = {
  All: ["Community Feed", "Hear the latest from fellow pack members. See everything the pack is sharing."],
  Announcements: ["Announcements", "Share a pupdate with the community."],
  Introductions: ["Introductions", "Introduce yourself, your pup, and what brings you to Social Paws."],
  Questions: ["Questions", "Ask the pack for advice or recs."],
  Requests: ["Requests", "Ask for help, a walk buddy, or some pup time."],
  Events: ["Events", "Find and join upcoming Social Paws events."],
};

const categoryHints = {
  Announcements: "See everything the pack is sharing.",
  Introductions: "Introduce yourself, your pup, and what brings you to Social Paws.",
  Questions: "Ask the pack for advice or recs.",
  Requests: "Ask for help, a walk buddy, or some pup time.",
};

export default function FeedPage() {
  const profile = useSelector((state) => state.user.profile);
  const [category, setCategory] = useState("All");
  const [heading, subtitle] = categoryHeadings[category] || categoryHeadings.All;
  const [posts, setPosts] = useState([]);
  const [feedsLoading, setFeedsLoading] = useState(true);
  const [feedsError, setFeedsError] = useState("");
  const [feedReload, setFeedReload] = useState(0);
  const feedRequestVersion = useRef(0);
  useEffect(() => {
    const controller = new AbortController();
    const version = ++feedRequestVersion.current;
    if (category === "Events") return;
    setPosts([]);
    setFeedsLoading(true);
    setFeedsError("");
    getAllFeeds({ signal: controller.signal, userId: profile?.id, category }).then((data) => {
      if (!controller.signal.aborted && version === feedRequestVersion.current) setPosts(data.map(normalizeFeed));
    }).catch((error) => {
      if (!controller.signal.aborted && version === feedRequestVersion.current) setFeedsError(error.response?.data?.message || error.message || "Unable to load the feed.");
    }).finally(() => {
      if (!controller.signal.aborted && version === feedRequestVersion.current) setFeedsLoading(false);
    });
    return () => controller.abort();
  }, [feedReload, category, profile?.id]);
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [eventsError, setEventsError] = useState("");
  const [eventsReload, setEventsReload] = useState(0);
  const [eventNotice, setEventNotice] = useState("");
  const [bookingEventId, setBookingEventId] = useState(null);
  const eventEmail = profile?.user_email?.trim() || "";
  const eventEmailRef = useRef(eventEmail);
  eventEmailRef.current = eventEmail;
  const bookingInFlight = useRef(false);
  const changeEventBooking = async (event) => {
    if (bookingInFlight.current) return;
    const bookingEmail = eventEmail;
    bookingInFlight.current = true;
    setBookingEventId(event.id);
    setEventNotice("");
    try {
      if (event.joined) {
        let bookingId = event.bookingId;
        if (!bookingId) {
          const fresh = await getAllEvents({ email: bookingEmail });
          const current = fresh.find((item) => item.id === event.id);
          bookingId = current?.bookingId;
          if (current && !current.joined) {
            if (eventEmailRef.current === bookingEmail) setEvents(fresh);
            return;
          }
        }
        await unjoinEvent(bookingId);
      } else {
        await bookEvent(event.id, profile);
      }
      if (eventEmailRef.current !== bookingEmail) return;
      setEvents((current) => current.map((item) => item.id === event.id
        ? { ...item, joined: !event.joined, bookingId: null } : item));
      setEventNotice(event.joined ? `You left ${event.title}.` : `You joined ${event.title}.`);
      try {
        const fresh = await getAllEvents({ email: bookingEmail });
        if (eventEmailRef.current === bookingEmail) setEvents(fresh);
      } catch {
        if (eventEmailRef.current === bookingEmail) setEventNotice("Booking updated, but the latest status could not load. Refresh events to verify.");
      }
    } catch (error) {
      if (eventEmailRef.current === bookingEmail) setEventNotice(`${event.title}: ${error.message}`);
    } finally {
      bookingInFlight.current = false;
      setBookingEventId(null);
    }
  };
  const joinEventButton = (event) => (
    <button className="feed-event-join"
      disabled={eventsLoading || !!eventsError || bookingEventId !== null}
      aria-busy={bookingEventId === event.id}
      onClick={() => changeEventBooking(event)}>
      {bookingEventId === event.id ? "UPDATING..." : event.joined ? "UNJOIN EVENT" : "JOIN EVENT"}
    </button>
  );
  const [selectedEventId, setSelectedEventId] = useState(null);
  const selectedEvent = events.find((event) => event.id === selectedEventId);
  const eventBackRef = useRef(null);
  useEffect(() => {
    if (category !== "Events") return;
    const controller = new AbortController();
    setEvents([]);
    setEventNotice("");
    setEventsLoading(true);
    setEventsError("");
    getAllEvents({ signal: controller.signal, email: eventEmail }).then((data) => {
      if (!controller.signal.aborted) setEvents(data);
    }).catch((error) => {
      if (!controller.signal.aborted) setEventsError(error.response?.data?.message || error.message || "Unable to load events.");
    }).finally(() => {
      if (!controller.signal.aborted) setEventsLoading(false);
    });
    return () => controller.abort();
  }, [category, eventsReload, eventEmail]);
  const [search, setSearch] = useState("");
  const [collapsed, setCollapsed] = useState(false);
  const [composer, setComposer] = useState(false);
  const [draft, setDraft] = useState("");
  const [postTitle, setPostTitle] = useState("");
  const [postCategory, setPostCategory] = useState("Introductions");
  const [selectedPostId, setSelectedPostId] = useState(null);
  const [reply, setReply] = useState("");
  const [replySubmitting, setReplySubmitting] = useState(false);
  const [replyError, setReplyError] = useState(null);
  const replySubmittingRef = useRef(false);
  const activePostRef = useRef(selectedPostId);
  activePostRef.current = selectedPostId;
  const selectedPost = posts.find((post) => post.id === selectedPostId);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [detailReload, setDetailReload] = useState(0);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentsError, setCommentsError] = useState("");
  const [commentsReload, setCommentsReload] = useState(0);
  const postsRef = useRef(posts);
  postsRef.current = posts;
  useEffect(() => {
    if (!selectedPostId) return;
    const controller = new AbortController();
    const initialIds = new Set((postsRef.current.find((post) => post.id === selectedPostId)?.comments || []).map((comment) => comment.id));
    setCommentsLoading(true);
    setCommentsError("");
    getFeedComments(selectedPostId, { signal: controller.signal }).then((data) => {
      if (controller.signal.aborted) return;
      const fetched = data.map(normalizeFeedComment);
      setPosts((current) => current.map((post) => {
        if (post.id !== selectedPostId) return post;
        const comments = mergeFetchedComments(fetched, post.comments, initialIds);
        return { ...post, comments, commentCount: comments.length, commentsLoaded: true };
      }));
    }).catch((error) => {
      if (!controller.signal.aborted) setCommentsError(error.response?.data?.message || error.message || "Unable to load comments.");
    }).finally(() => {
      if (!controller.signal.aborted) setCommentsLoading(false);
    });
    return () => controller.abort();
  }, [selectedPostId, commentsReload, detailReload]);
  useEffect(() => {
    if (!selectedPostId) return;
    const controller = new AbortController();
    setDetailLoading(true);
    setDetailError("");
    getFeedById(selectedPostId, { signal: controller.signal }).then((data) => {
      if (controller.signal.aborted) return;
      const fresh = normalizeFeed(data);
      setPosts((current) => current.map((post) => post.id === fresh.id
        ? { ...fresh, comments: post.comments, commentsLoaded: post.commentsLoaded, commentCount: post.commentsLoaded ? post.comments.length : Math.max(fresh.commentCount, post.commentCount), liked: post.liked, likes: fresh.likes + (post.liked ? 1 : 0) }
        : post));
    }).catch((error) => {
      if (!controller.signal.aborted) setDetailError(error.response?.status === 404
        ? "This post is no longer available."
        : error.response?.data?.message || error.message || "Unable to load this post.");
    }).finally(() => {
      if (!controller.signal.aborted) setDetailLoading(false);
    });
    return () => controller.abort();
  }, [selectedPostId, detailReload]);
  const [notice, setNotice] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isReadingPhotos, setIsReadingPhotos] = useState(false);
  const [publishError, setPublishError] = useState("");
  const publishingRef = useRef(false);
  const [photoError, setPhotoError] = useState("");
  const mainRef = useRef(null);
  const backRef = useRef(null);
  const replyRef = useRef(null);
  const fileInput = useRef(null);
  const photoReadVersion = useRef(0);
  const author = profile?.user_name || profile?.first_name || profile?.name || "You";
  const visiblePosts = posts.filter((post) => (category === "All" || post.category === category) && `${post.author} ${post.title || ""} ${post.text} ${post.category}`.toLowerCase().includes(search.toLowerCase()));

  const openEvent = (event, id) => {
    if (event.target.closest("button, a")) return;
    if (event.type === "keydown") {
      if (event.currentTarget !== event.target || !["Enter", " "].includes(event.key)) return;
      event.preventDefault();
    }
    setSelectedEventId(id);
    setEventNotice("");
    requestAnimationFrame(() => {
      mainRef.current?.scrollTo({ top: 0 });
      eventBackRef.current?.focus({ preventScroll: true });
    });
  };
  const shareEvent = async () => {
    try {
      await navigator.clipboard.writeText(`${selectedEvent.title} — ${selectedEvent.date} ${selectedEvent.registrationUrl}`);
      setEventNotice("Event details copied. Paste them to share with your pack.");
    } catch { setEventNotice("Unable to copy. Please select and copy the event details."); }
  };

  const openPost = (id) => {
    setDetailLoading(true);
    setDetailError("");
    setCommentsLoading(true);
    setCommentsError("");
    setSelectedPostId(id);
    setReply("");
    requestAnimationFrame(() => {
      mainRef.current?.scrollTo({ top: 0 });
      backRef.current?.focus({ preventScroll: true });
    });
  };
  const handleCardOpen = (event, id) => {
    if (event.target.closest("button, input, textarea, a")) return;
    if (event.type === "keydown") {
      if (event.target !== event.currentTarget || !["Enter", " "].includes(event.key)) return;
      event.preventDefault();
    }
    openPost(id);
  };
  const backToFeed = () => {
    const id = selectedPostId;
    setSelectedPostId(null);
    setReply("");
    requestAnimationFrame(() => document.getElementById(id)?.focus());
  };

  const resetComposer = () => {
    photoReadVersion.current += 1;
    setIsReadingPhotos(false);
    setComposer(false);
    setDraft("");
    setPostTitle("");
    setAttachments([]);
    setPhotoError("");
    setPublishError("");
    setPostCategory("Introductions");
  };
  const closeComposer = () => {
    if (!publishingRef.current) resetComposer();
  };
  const attachPhoto = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (!files.length || publishingRef.current) return;
    if (files.some((file) => !["image/jpeg", "image/png", "image/webp", "image/gif"].includes(file.type) || file.size > 10 * 1024 * 1024)) {
      setPhotoError("Choose JPG, PNG, WebP, or GIF images under 10 MB each.");
      return;
    }
    const version = ++photoReadVersion.current;
    setPhotoError("");
    setIsReadingPhotos(true);
    try {
      const photos = await Promise.all(files.map((file) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve({ file, url: reader.result, name: file.name });
        reader.onerror = reject;
        reader.readAsDataURL(file);
      })));
      if (version === photoReadVersion.current) setAttachments((current) => [...current, ...photos]);
    } catch {
      if (version === photoReadVersion.current) setPhotoError("Unable to read these images. Please try again.");
    } finally {
      if (version === photoReadVersion.current) setIsReadingPhotos(false);
    }
  };

  const updatePost = (id, update) => setPosts((current) => current.map((post) => post.id === id ? update(post) : post));
  const submitReply = async (event) => {
    event.preventDefault();
    if (replySubmittingRef.current || !reply.trim() || !selectedPostId) return;
    const feedId = selectedPostId;
    const text = reply.trim();
    replySubmittingRef.current = true;
    setReplySubmitting(true);
    setReplyError(null);
    try {
      const saved = await createFeedComment({ userId: profile?.id, feedId, comment: text });
      const comment = normalizeFeedComment(saved);
      updatePost(feedId, (post) => post.comments.some((item) => item.id === comment.id) ? post : {
        ...post, comments: [...post.comments, comment], commentCount: post.commentCount + 1,
      });
      if (activePostRef.current === feedId) setReply("");
    } catch (error) {
      setReplyError({ feedId, message: error.message });
    } finally {
      replySubmittingRef.current = false;
      setReplySubmitting(false);
    }
  };

  const publish = async (event) => {
    event.preventDefault();
    if (publishingRef.current || isReadingPhotos || !draft.trim()) return;
    publishingRef.current = true;
    setIsPublishing(true);
    setPublishError("");
    try {
      const result = await createFeed({ userId: profile?.id, title: postTitle, description: draft, category: postCategory, images: attachments.map((photo) => photo.file) });
      const saved = result.data;
      feedRequestVersion.current += 1;
      setFeedsLoading(false);
      setFeedsError("");
      const created = normalizeFeed({ ...saved, user_name: profile?.user_name || author, user_profile_image: profile?.user_profile_image });
      setPosts((current) => [created, ...current.filter((post) => post.id !== created.id)]);
      resetComposer();
      setCategory("All"); setSearch(""); setSelectedPostId(null); setSelectedEventId(null);
      setNotice("Your post was published successfully.");
    } catch (error) {
      setPublishError(error.message || "Unable to publish your post. Please try again.");
    } finally {
      publishingRef.current = false;
      setIsPublishing(false);
    }
  };
  const share = async (post) => {
    const url = `${window.location.origin}/feed#${post.id}`;
    try {
      await navigator.clipboard.writeText(`${post.author}: ${post.text}\n${url}`);
      setNotice("Post copied to clipboard.");
    } catch {
      setNotice("Sharing is unavailable in this browser. You can copy the post text directly.");
    }
  };

  return (
    <div className={`feed-page feed-mobile-layout ${category === "Events" && selectedEvent ? "feed-mobile-event-detail" : ""} ${selectedPostId ? "feed-mobile-detail" : category === "Events" && !selectedEvent ? "feed-mobile-events" : ""} ${composer ? "feed-composing" : ""}`}>
      <header className="feed-header">
        <Link to="/help" className="feed-mobile-help">Need Help?</Link>
        <Link to="/dashboard-switcher" className="feed-brand"><img src={appIcon} alt="" />Social Paws</Link>
        <TopNavBar theme="dark" mobileFeed />
      </header>
      <div className={`feed-layout ${collapsed ? "feed-layout-collapsed" : ""}`}>
        <aside className="feed-sidebar" aria-label="Pack filters">
          <div className="feed-filter-heading">{!collapsed && <span>PACK FILTERS</span>}<button aria-label={collapsed ? "Expand filters" : "Collapse filters"} onClick={() => setCollapsed(!collapsed)}><img src={filterToggleIcon} alt="" width={23} height={23} style={{ transform: collapsed ? "rotate(180deg)" : undefined }} /></button></div>
          {!collapsed && <>
            <nav className="feed-filters" aria-label="Post categories">{categories.map((item) => <button key={item} aria-pressed={category === item} className={category === item ? "selected" : ""} onClick={() => { setCategory(item); setSelectedPostId(null); setSelectedEventId(null); }}><img src={item === "Events" ? eventsIcon : category === item ? dogSelectedIcon : dogUnselectedIcon} alt="" width={17} height={item === "Events" ? 19 : 16} style={{ flexShrink: 0 }} />{item}</button>)}</nav>
            <button className="feed-event" onClick={() => { setCategory("Events"); setSelectedPostId(null); setSelectedEventId(null); }}><strong>Local Pack Walk</strong><span>Join other dog lovers this Saturday at Central Park!</span></button>
          </>}
        </aside>
        <main className="feed-main" ref={mainRef}>
          {category === "Events" && selectedEvent ? <section className="feed-event-detail" aria-label="Event details">
            <button ref={eventBackRef} className="feed-back" onClick={() => {
              const id = selectedEventId;
              setSelectedEventId(null);
              setEventNotice("");
              requestAnimationFrame(() => document.getElementById(`event-${id}`)?.focus());
            }}><ArrowLeft size={22} />Back to Events</button>
            {eventNotice && <div className="feed-notice" role="status">{eventNotice}<button aria-label="Dismiss message" onClick={() => setEventNotice("")}><X size={16} /></button></div>}
            <div className="feed-event-detail-card">
              {selectedEvent.image && <img className="feed-event-banner feed-api-event-image" src={selectedEvent.image} alt={selectedEvent.title} />}
              <div className="feed-event-detail-title"><div><h1>{selectedEvent.title}</h1><div className="feed-event-meta"><span><CalendarDays size={15} />{selectedEvent.date}</span></div></div>{selectedEvent.price && <span className="feed-event-spots">{selectedEvent.price}</span>}</div>
              <div className="feed-event-registration">
                {joinEventButton(selectedEvent)}
              </div>
              <div className="feed-event-description"><h2>About the Event</h2><p className="feed-api-event-description">{selectedEvent.description}</p></div>
              <button className="feed-event-share" onClick={shareEvent}>Share with your pack</button>
            </div>
          </section> : selectedPostId ? <section className="feed-detail" aria-label="Post details">
            <button ref={backRef} className="feed-back" onClick={backToFeed}><ArrowLeft size={23} /><span className="feed-back-desktop-label">BACK TO FEED</span><span className="feed-back-mobile-label">Back to Feed</span></button>
            {detailLoading ? <p role="status">Loading post...</p> : detailError ? <div className="feed-notice" role="alert"><span>{detailError}</span><button onClick={() => setDetailReload((value) => value + 1)}>Retry</button></div> : selectedPost ? <>

            <article className="feed-post">
              <div className="feed-post-heading"><img className="feed-avatar" src={selectedPost.avatar || cardAvatar} onError={(event) => { event.currentTarget.onerror = null; event.currentTarget.src = cardAvatar; }} alt="" /><div><h2>{selectedPost.author}</h2><span className="feed-time">{selectedPost.time}</span></div><span className={`feed-category ${selectedPost.category === "Announcements" ? "announcement" : ""}`}>{selectedPost.category}</span></div>
              {selectedPost.title && <h3 className="feed-post-title">{selectedPost.title}</h3>}
              <p className="feed-post-text">{selectedPost.text}</p>
              {selectedPost.images?.map((url, index) => <img key={index} className="feed-post-image" src={url} alt={`Post attachment ${index + 1}`} />)}
              {selectedPost.image && <img className="feed-post-image" src={selectedPost.image} alt={selectedPost.imageAlt || "Community post photo"} />}
              <div className="feed-actions"><button aria-label={`Give ${selectedPost.author} a bone`} aria-pressed={!!selectedPost.liked} onClick={() => updatePost(selectedPost.id, (post) => ({ ...post, liked: !post.liked, likes: post.likes + (post.liked ? -1 : 1) }))}><img src={boneIcon} alt="" width={19} height={19} />{selectedPost.likes}</button><button onClick={() => replyRef.current?.focus()}><img src={commentIcon} alt="" width={19} height={19} />{selectedPost.commentCount} Comments</button><button className="feed-detail-share" onClick={() => share(selectedPost)}><img src={shareIcon} alt="" width={20} height={20} />Share</button></div>
            </article>
            <h2 className="feed-comments-title">COMMENTS</h2>
            <div className="feed-detail-comments" aria-live="polite">
              {commentsLoading && <p role="status">Loading comments...</p>}
              {commentsError && <div className="feed-notice" role="alert"><span>{commentsError}</span><button onClick={() => setCommentsReload((value) => value + 1)}>Retry comments</button></div>}
              {!commentsLoading && !commentsError && selectedPost.comments.length === 0 && <p className="feed-no-comments">No comments yet. Be the first to reply!</p>}
              {selectedPost.comments.map((comment, index) => <article className="feed-detail-comment" key={comment.id || index}><img src={comment.avatar || userPlaceholder} onError={(event) => { event.currentTarget.onerror = null; event.currentTarget.src = userPlaceholder; }} alt="" /><div><div className="feed-comment-author"><strong>{comment.author}</strong><span>{comment.time || "Just now"}</span></div><p>{comment.text}</p></div></article>)}
            </div>
            {replyError?.feedId === selectedPostId && <p className="feed-photo-error" role="alert">{replyError.message}</p>}
            <form className="feed-detail-reply" onSubmit={submitReply} aria-busy={replySubmitting}><img src={composeAvatar} alt="" /><div><input disabled={replySubmitting} ref={replyRef} value={reply} onChange={(event) => setReply(event.target.value)} placeholder="Write a reply to the pack..." aria-label="Write a reply to the pack" maxLength={1000} required /><button type="submit" aria-label={replySubmitting ? "Sending reply" : "Send reply"} disabled={!reply.trim() || replySubmitting}><img src={sendIcon} alt="" width={18} height={17} /></button></div></form>
            </> : <p role="alert">This post is not available.</p>}
          </section> : category === "Events" ? <section className="feed-events" aria-labelledby="feed-events-title">
            <div className="feed-title-row"><div><h1 id="feed-events-title">{heading}</h1><p>{subtitle}</p></div></div>
            <nav className="feed-mobile-categories" aria-label="Mobile event categories">{["Events", "All", "Introductions", "Questions", "Announcements", "Requests"].map((item) => <button key={item} aria-pressed={category === item} onClick={() => { setCategory(item); setSelectedPostId(null); setSelectedEventId(null); }}>{item}</button>)}</nav>
            {eventNotice && <div className="feed-notice" role="status">{eventNotice}<button aria-label="Dismiss message" onClick={() => setEventNotice("")}><X size={16} /></button></div>}
            {eventsLoading ? <p role="status">Loading events...</p> : eventsError ? <div className="feed-notice" role="alert"><span>{eventsError}</span><button onClick={() => setEventsReload((value) => value + 1)}>Retry</button></div> : events.length === 0 ? <p>No events available yet.</p> : <div className="feed-event-list">{events.map((event) => (
              <article className="feed-event-card feed-post-clickable" key={event.id} id={`event-${event.id}`} tabIndex={0} aria-label={`${event.title}. Press Enter for event details.`} onClick={(e) => openEvent(e, event.id)} onKeyDown={(e) => openEvent(e, event.id)}>
                {event.image && <img className="feed-event-photo feed-api-event-image" src={event.image} alt={event.title} loading="lazy" />}
                <div className="feed-event-content">
                  <div className="feed-event-title-row"><h2>{event.title}</h2>{event.price && <span className="feed-event-spots">{event.price}</span>}</div>
                  <div className="feed-event-meta"><span><CalendarDays size={16} />{event.date}</span></div>
                  <p className="feed-api-event-description">{event.description}</p>
                  {joinEventButton(event)}
                </div>
              </article>
            ))}</div>}
          </section> : <>

          <div className="feed-title-row"><div><h1>{heading}</h1><p>{subtitle}</p></div><label className="feed-search"><Search size={19} /><input aria-label="Search feed" placeholder="Search feed..." value={search} onChange={(event) => setSearch(event.target.value)} /></label></div>
          {!composer && <nav className="feed-mobile-categories" aria-label="Mobile post categories">{["Events", "All", "Introductions", "Questions", "Announcements", "Requests"].map((item) => <button key={item} aria-pressed={category === item} onClick={() => { setCategory(item); setSelectedPostId(null); setSelectedEventId(null); }}>{item}</button>)}</nav>}
          {composer ? (
            <form className="feed-inline-composer" onSubmit={publish} aria-labelledby="feed-compose-title">
              <div className="feed-composer-heading">
                <img src={composeAvatar} alt="" />
                <h2 id="feed-compose-title">CREATE NEW POST</h2>
                <button className="feed-composer-close" type="button" aria-label="Close new post" onClick={closeComposer} disabled={isPublishing}><img src={closeComposerIcon} alt="" width={30} height={30} /></button>
              </div>
              <label className="feed-post-title-field">Title (optional)<input disabled={isPublishing} value={postTitle} onChange={(event) => setPostTitle(event.target.value)} placeholder="Give your post a title" /></label>
              <textarea disabled={isPublishing} autoFocus aria-label="Post content" value={draft} onChange={(event) => setDraft(event.target.value)} placeholder="Share something with the pack..." required maxLength={3000} rows={4} />
              <div className="feed-photo-heading"><span>{attachments.length ? "ATTACHED PHOTO" : "ATTACH PHOTO"}</span></div>
              <input ref={fileInput} type="file" accept="image/jpeg,image/png,image/webp,image/gif" multiple hidden onChange={attachPhoto} disabled={isPublishing || isReadingPhotos} />
              <div className="feed-attachments">{attachments.map((photo, index) => <div key={`${photo.name}-${index}`}><img className="feed-attachment-preview" src={photo.url} alt="Attachment preview" /><button type="button" disabled={isPublishing} onClick={() => setAttachments((current) => current.filter((_, i) => i !== index))}><img src={removePhotoIcon} alt="" width={14} height={16} />Remove</button></div>)}</div>
              <button type="button" className="feed-photo-add" disabled={isPublishing || isReadingPhotos} onClick={() => fileInput.current?.click()}><ImagePlus size={22} />{isReadingPhotos ? "Reading photos..." : attachments.length ? "Add more photos" : "Add photo"}</button>
              {photoError && <p className="feed-photo-error" role="alert">{photoError}</p>}
              <fieldset className="feed-category-picker" disabled={isPublishing} aria-describedby="feed-category-hint"><legend>CHOOSE CATEGORY</legend>
                {["Announcements", "Introductions", "Questions", "Requests"].map((item) => <button key={item} type="button" aria-pressed={postCategory === item} className={postCategory === item ? "selected" : ""} onClick={() => setPostCategory(item)}>{item}</button>)}
                <p id="feed-category-hint" className="feed-category-hint" aria-live="polite">{categoryHints[postCategory]}</p>
              </fieldset>
              {publishError && <p className="feed-photo-error" role="alert">{publishError}</p>}
              <div className="feed-composer-footer"><button type="button" className="feed-composer-cancel" onClick={closeComposer} disabled={isPublishing}>Cancel</button><button className="feed-primary" type="submit" disabled={!draft.trim() || isPublishing || isReadingPhotos}><Plus size={24} strokeWidth={2} aria-hidden="true" />{isPublishing ? "POSTING..." : "POST NOW"}</button></div>
            </form>
          ) : <div className="feed-compose-row"><label className="feed-mobile-search"><Search size={18} /><input aria-label="Search pack posts and events" placeholder="Search pack posts, events..." value={search} onChange={(event) => setSearch(event.target.value)} /></label><button className="feed-compose-prompt" onClick={() => setComposer(true)}><img src={composeAvatar} alt="" /><span>Share something with the pack...</span></button><button className="feed-primary" onClick={() => setComposer(true)}><Plus size={21} />NEW POST</button></div>}
          {notice && <div className="feed-notice" role="status">{notice}<button aria-label="Dismiss message" onClick={() => setNotice("")}><X size={16} /></button></div>}
          {feedsLoading && <p role="status">Loading posts...</p>}
          {feedsError && <div className="feed-notice" role="alert"><span>{feedsError}</span><button onClick={() => setFeedReload((value) => value + 1)}>Retry</button></div>}
          <div className="feed-posts">
            {visiblePosts.map((post) => <article className="feed-post feed-post-clickable" key={post.id} id={post.id} tabIndex={0} aria-label={`Post by ${post.author}. Press Enter to view post and comments.`} onClick={(event) => handleCardOpen(event, post.id)} onKeyDown={(event) => handleCardOpen(event, post.id)}>
              <div className="feed-post-heading"><img className="feed-avatar" src={post.avatar || cardAvatar} onError={(event) => { event.currentTarget.onerror = null; event.currentTarget.src = cardAvatar; }} alt="" /><div><h2>{post.author}</h2><span className="feed-time">{post.time}</span></div><span className={`feed-category ${post.category === "Announcements" ? "announcement" : ""}`}>{post.category}</span></div>
              {post.title && <h3 className="feed-post-title">{post.title}</h3>}
              <p className="feed-post-text">{post.text}</p>
              {post.images?.map((url, index) => <img key={index} className="feed-post-image" src={url} alt={`Post attachment ${index + 1}`} />)}
              {post.image && <img className="feed-post-image" src={post.image} alt={post.imageAlt || "A green park with trees and paths for a community walk"} loading="lazy" />}
              <div className="feed-actions"><button aria-label={`Give ${post.author} a bone`} aria-pressed={!!post.liked} onClick={() => updatePost(post.id, (item) => ({ ...item, liked: !item.liked, likes: item.likes + (item.liked ? -1 : 1) }))}><img src={boneIcon} alt="" width={19} height={19} />{post.likes}</button><button onClick={() => openPost(post.id)}><img src={commentIcon} alt="" width={19} height={19} />{post.commentCount} Comments</button><button onClick={() => share(post)}><img src={shareIcon} alt="" width={20} height={20} />Share</button></div>

            </article>)}
            {!feedsLoading && !feedsError && visiblePosts.length === 0 && <div className="feed-empty"><Dog size={32} /><h2>No posts yet</h2><p>Try another search or start a conversation with the pack.</p><button onClick={() => { setCategory("All"); setSearch(""); }}>Show all posts</button></div>}
          </div>
          </>}
        </main>
      </div>


    </div>
  );
}
